import { useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';

import type { BusinessProfile } from '../../../bridge/types/business-profile.ts';

interface BusinessProfileDoc {
	business_profile: BusinessProfile;
}

const cache = new Map<string, Promise<string | null>>();

// The cached business name is fetch-once per user and shared across mounts.
// After a rename, drop the entry so the next mount of a read surface (chat
// greeting, Settings label) refetches instead of showing the old name.
export const invalidateBusinessName = (userId: string) => {
	cache.delete(userId);
};

interface UseBusinessNameResult {
	businessName: string | null;
	loading: boolean;
}

export const useBusinessName = (): UseBusinessNameResult => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [businessName, setBusinessName] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(!!userId);

	useEffect(() => {
		if (!userId) {
			setBusinessName(null);
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);

		let pending = cache.get(userId);
		if (!pending) {
			pending = bridgewayApi
				.get<BusinessProfileDoc>(`/api/user/${userId}/business-profile`)
				.then(
					(doc) =>
						doc?.business_profile?.my_business?.business_name?.value ?? null,
				)
				.catch(() => null);
			cache.set(userId, pending);
		}

		void pending.then((name) => {
			if (cancelled) return;
			setBusinessName(name);
			setLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [userId, bridgewayApi]);

	return { businessName, loading };
};
